'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@/utils/tailwindcss-override';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;
const SelectTrigger = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.Trigger>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>>((t0, reference) => {
  const $ = _c(12);
  let children;
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex h-9 w-full items-center justify-between rounded-md border border-gray-200 bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs placeholder:text-neutral-500 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-800 dark:placeholder:text-neutral-400 [&>span]:line-clamp-1", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SelectPrimitive.Icon asChild={true}><ChevronDown className="h-4 w-4 opacity-50" /></SelectPrimitive.Icon>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== properties || $[9] !== reference || $[10] !== t1) {
    t3 = <SelectPrimitive.Trigger ref={reference} className={t1} {...properties}>{children}{t2}</SelectPrimitive.Trigger>;
    $[7] = children;
    $[8] = properties;
    $[9] = reference;
    $[10] = t1;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectScrollUpButton = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.ScrollUpButton>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>>((t0, reference) => {
  const $ = _c(10);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUp className="h-4 w-4" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== properties || $[7] !== reference || $[8] !== t1) {
    t3 = <SelectPrimitive.ScrollUpButton ref={reference} className={t1} {...properties}>{t2}</SelectPrimitive.ScrollUpButton>;
    $[6] = properties;
    $[7] = reference;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
});
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.ScrollDownButton>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>>((t0, reference) => {
  const $ = _c(10);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex cursor-default items-center justify-center py-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDown className="h-4 w-4" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== properties || $[7] !== reference || $[8] !== t1) {
    t3 = <SelectPrimitive.ScrollDownButton ref={reference} className={t1} {...properties}>{t2}</SelectPrimitive.ScrollDownButton>;
    $[6] = properties;
    $[7] = reference;
    $[8] = t1;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
});
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
const SelectContent = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.Content>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>>((t0, reference) => {
  const $ = _c(21);
  let children;
  let className;
  let properties;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
    t1 = $[4];
  }
  const position = t1 === undefined ? "popper" : t1;
  const t2 = position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1";
  let t3;
  if ($[5] !== className || $[6] !== t2) {
    t3 = cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-gray-200 bg-white text-neutral-950 shadow-md dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50", t2, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SelectScrollUpButton />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]";
  let t6;
  if ($[9] !== t5) {
    t6 = cn("p-1", t5);
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children || $[12] !== t6) {
    t7 = <SelectPrimitive.Viewport className={t6}>{children}</SelectPrimitive.Viewport>;
    $[11] = children;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SelectScrollDownButton />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== position || $[16] !== properties || $[17] !== reference || $[18] !== t3 || $[19] !== t7) {
    t9 = <SelectPrimitive.Portal><SelectPrimitive.Content ref={reference} className={t3} position={position} {...properties}>{t4}{t7}{t8}</SelectPrimitive.Content></SelectPrimitive.Portal>;
    $[15] = position;
    $[16] = properties;
    $[17] = reference;
    $[18] = t3;
    $[19] = t7;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
});
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.Label>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>>((t0, reference) => {
  const $ = _c(9);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-2 py-1.5 text-sm font-semibold", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== properties || $[6] !== reference || $[7] !== t1) {
    t2 = <SelectPrimitive.Label ref={reference} className={t1} {...properties} />;
    $[5] = properties;
    $[6] = reference;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.Item>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>>((t0, reference) => {
  const $ = _c(14);
  let children;
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = properties;
  } else {
    children = $[1];
    className = $[2];
    properties = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex w-full cursor-default items-center rounded-xs py-1.5 pr-8 pl-2 text-sm outline-hidden select-none focus:bg-neutral-100 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:focus:bg-neutral-800 dark:focus:text-neutral-50", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center"><SelectPrimitive.ItemIndicator><Check className="h-4 w-4" /></SelectPrimitive.ItemIndicator></span>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children) {
    t3 = <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>;
    $[7] = children;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== properties || $[10] !== reference || $[11] !== t1 || $[12] !== t3) {
    t4 = <SelectPrimitive.Item ref={reference} className={t1} {...properties}>{t2}{t3}</SelectPrimitive.Item>;
    $[9] = properties;
    $[10] = reference;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
});
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = React.forwardRef<React.ComponentRef<typeof SelectPrimitive.Separator>, React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>>((t0, reference) => {
  const $ = _c(9);
  let className;
  let properties;
  if ($[0] !== t0) {
    ({
      className,
      ...properties
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = properties;
  } else {
    className = $[1];
    properties = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("-mx-1 my-1 h-px bg-neutral-100 dark:bg-neutral-800", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== properties || $[6] !== reference || $[7] !== t1) {
    t2 = <SelectPrimitive.Separator ref={reference} className={t1} {...properties} />;
    $[5] = properties;
    $[6] = reference;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };